<template>
  <div class="admin-machinery">
    <!-- Header -->
    <div class="admin-header">
      <h1 class="admin-title">
        <i class="fas fa-cogs"></i>
        Makine Parkı Yönetimi
      </h1>
      <button @click="openCreateModal" class="btn btn-create">
        <i class="fas fa-plus"></i>
        Yeni Makine Ekle
      </button>
    </div>

    <!-- Statistics Cards -->
    <div class="stats-grid">
      <div class="stat-card">
        <div class="stat-icon">
          <i class="fas fa-cogs"></i>
        </div>
        <div class="stat-content">
          <div class="stat-number">{{ totalMachinery }}</div>
          <div class="stat-label">Toplam Makine</div>
        </div>
      </div>
      <div class="stat-card">
        <div class="stat-icon active">
          <i class="fas fa-check-circle"></i>
        </div>
        <div class="stat-content">
          <div class="stat-number">{{ activeMachinery }}</div>
          <div class="stat-label">Aktif Makine</div>
        </div>
      </div>
    </div>

    <!-- Filters -->
    <div class="admin-filters">
      <div class="search-box">
        <i class="fas fa-search"></i>
        <input 
          v-model="searchQuery" 
          type="text" 
          placeholder="Makine ara..."
          @input="debouncedSearch"
        />
      </div>
      <select v-model="filterActive" class="filter-select">
        <option value="">Tüm Makineler</option>
        <option value="1">Aktif</option>
        <option value="0">Pasif</option>
      </select>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>Makineler yükleniyor...</p>
    </div>

    <!-- Empty State -->
    <div v-else-if="machinery.length === 0" class="empty-state">
      <i class="fas fa-cogs"></i>
      <h3>Henüz makine eklenmemiş</h3>
      <p>İlk makineyi eklemek için "Yeni Makine Ekle" butonuna tıklayın.</p>
    </div>

    <!-- Machinery Grid -->
    <div v-else class="admin-machinery-grid">
      <div 
        v-for="machine in machinery" 
        :key="machine.id" 
        class="machinery-card"
      >
        <div class="machinery-image">
          <img 
            :src="machine.image_url || '/images/default-machine.jpg'" 
            :alt="machine.name_tr"
            @error="handleImageError"
          />
        </div>
        <div class="machinery-info">
          <div class="machinery-brand">{{ machine.brand }}</div>
          <h3 class="machinery-name">{{ machine.name_tr }}</h3>
          <p class="machinery-name-en">{{ machine.name_en }}</p>
          
          <div class="machinery-specs">
            <div v-if="machine.drilling_diameter" class="spec-item">
              <i class="fas fa-circle"></i>
              <span>Delme Çapı: {{ machine.drilling_diameter }}</span>
            </div>
            <div v-if="machine.drilling_depth" class="spec-item">
              <i class="fas fa-arrows-alt-v"></i>
              <span>Delme Derinliği: {{ machine.drilling_depth }}</span>
            </div>
            <div v-if="machine.motor_power" class="spec-item">
              <i class="fas fa-bolt"></i>
              <span>Motor Gücü: {{ machine.motor_power }}</span>
            </div>
            <div v-if="machine.weight" class="spec-item">
              <i class="fas fa-weight-hanging"></i>
              <span>Ağırlık: {{ machine.weight }}</span>
            </div>
          </div>

          <div class="machinery-meta">
            <span :class="['machinery-status', machine.is_active ? 'active' : 'inactive']">
              {{ machine.is_active ? 'Aktif' : 'Pasif' }}
            </span>
            <span class="machinery-order">Sıra: {{ machine.sort_order }}</span>
          </div>
        </div>
        
        <div class="machinery-actions">
          <button 
            @click="toggleActive(machine)" 
            :class="['btn-status', machine.is_active ? 'active' : 'inactive']"
            :title="machine.is_active ? 'Pasif Yap' : 'Aktif Yap'"
          >
            <i :class="machine.is_active ? 'fas fa-eye-slash' : 'fas fa-eye'"></i>
          </button>
          <button @click="openEditModal(machine)" class="btn-edit" title="Düzenle">
            <i class="fas fa-edit"></i>
          </button>
          <button @click="deleteMachine(machine)" class="btn-delete" title="Sil">
            <i class="fas fa-trash"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Modal -->
    <div v-if="showModal" class="modal-overlay" @click="closeModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h2>{{ isEditing ? 'Makine Düzenle' : 'Yeni Makine Ekle' }}</h2>
          <button @click="closeModal" class="modal-close">
            <i class="fas fa-times"></i>
          </button>
        </div>
        
        <form @submit.prevent="submitForm" class="modal-form">
          <div class="form-row">
            <div class="form-group">
              <label>Marka *</label>
              <input v-model="form.brand" type="text" required />
            </div>
            <div class="form-group">
              <label>Makine Adı (TR) *</label>
              <input v-model="form.name_tr" type="text" required />
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>Makine Adı (EN) *</label>
              <input v-model="form.name_en" type="text" required />
            </div>
            <div class="form-group">
              <label>Kategori (TR)</label>
              <input v-model="form.category_tr" type="text" />
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>Kategori (EN)</label>
              <input v-model="form.category_en" type="text" />
            </div>
            <div class="form-group">
              <label>Resim URL</label>
              <input v-model="form.image_url" type="url" />
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>Delme Çapı</label>
              <input v-model="form.drilling_diameter" type="text" />
            </div>
            <div class="form-group">
              <label>Delme Derinliği</label>
              <input v-model="form.drilling_depth" type="text" />
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>Motor Gücü</label>
              <input v-model="form.motor_power" type="text" />
            </div>
            <div class="form-group">
              <label>Ağırlık</label>
              <input v-model="form.weight" type="text" />
            </div>
          </div>

          <div class="form-group">
            <label>Özellikler (TR)</label>
            <textarea v-model="form.features_tr" rows="3" placeholder="JSON formatında özellikler"></textarea>
          </div>

          <div class="form-group">
            <label>Özellikler (EN)</label>
            <textarea v-model="form.features_en" rows="3" placeholder="JSON formatında özellikler"></textarea>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>Sıra Numarası</label>
              <input v-model="form.sort_order" type="number" min="0" />
            </div>
            <div class="form-group">
              <label>
                <input v-model="form.is_active" type="checkbox" />
                Aktif
              </label>
            </div>
          </div>

          <div class="form-actions">
            <button type="button" @click="closeModal" class="btn btn-cancel">İptal</button>
            <button type="submit" class="btn btn-primary" :disabled="submitting">
              {{ submitting ? 'Kaydediliyor...' : (isEditing ? 'Güncelle' : 'Ekle') }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, computed, watch } from 'vue'
import { adminApi } from '@/services/adminApi'

const emit = defineEmits(['statsUpdated'])

// Data
const machinery = ref([])
const loading = ref(false)
const submitting = ref(false)
const showModal = ref(false)
const isEditing = ref(false)
const searchQuery = ref('')
const filterActive = ref('')

// Real database totals (not affected by filters)
const realTotalMachinery = ref(0)
const realActiveMachinery = ref(0)

// Pagination
const currentPage = ref(1)
const totalPages = ref(1)
const itemsPerPage = ref(10)

// Form data
const form = reactive({
  id: null,
  brand: '',
  name_tr: '',
  name_en: '',
  category_tr: '',
  category_en: '',
  image_url: '',
  drilling_diameter: '',
  drilling_depth: '',
  motor_power: '',
  weight: '',
  specifications: '',
  features_tr: '',
  features_en: '',
  sort_order: 0,
  is_active: true
})

// Computed
const totalMachinery = computed(() => realTotalMachinery.value)
const activeMachinery = computed(() => realActiveMachinery.value)

// Methods
const loadMachinery = async () => {
  try {
    loading.value = true
    
    const params = {
      page: currentPage.value,
      limit: itemsPerPage.value
    }
    
    if (searchQuery.value) {
      params.search = searchQuery.value
    }
    
    if (filterActive.value !== '') {
      params.active = filterActive.value
    }
    
    const response = await adminApi.getMachinery(params)
    console.log('Raw machinery API response:', response)
    
    if (response.success) {
      // Duplicate kontrolü ile makineleri filtrele
      const rawMachinery = response.data.machinery || []
      console.log('Raw machinery data:', rawMachinery)
      
      const uniqueMachinery = rawMachinery.filter((machine, index, self) => 
        index === self.findIndex(m => m.id === machine.id)
      ).map(machine => {
        // is_active alanını düzgün şekilde boolean'a çevir
        const isActive = machine.is_active === 1 || machine.is_active === '1' || machine.is_active === true
        console.log(`Machine ${machine.id} - Original is_active:`, machine.is_active, '→ Converted:', isActive)
        
        return {
          ...machine,
          is_active: isActive
        }
      })
      
      console.log('Processed machinery data:', uniqueMachinery)
      machinery.value = uniqueMachinery
      
      // Real totals from API (not affected by filters)
      realTotalMachinery.value = response.data.total_all_machinery || 0
      realActiveMachinery.value = response.data.total_active_machinery || 0
      
      // Pagination bilgilerini güncelle
      if (response.data.pagination) {
        currentPage.value = response.data.pagination.current_page
        totalPages.value = response.data.pagination.total_pages
      }
      
      await updateStats()
    } else {
      throw new Error(response.message || 'Makine verileri alınamadı')
    }
  } catch (error) {
    console.error('Makineler yüklenemedi:', error)
    // Toast veya alert ile kullanıcıya bilgi ver
  } finally {
    loading.value = false
  }
}

const updateStats = async () => {
  emit('statsUpdated', {
    total: realTotalMachinery.value,
    active: realActiveMachinery.value,
    inactive: realTotalMachinery.value - realActiveMachinery.value
  })
}

const openCreateModal = () => {
  resetForm()
  isEditing.value = false
  showModal.value = true
}

const openEditModal = (machine) => {
  resetForm()
  Object.assign(form, {
    ...machine,
    features_tr: Array.isArray(machine.features_tr) ? machine.features_tr.join(', ') : machine.features_tr || '',
    features_en: Array.isArray(machine.features_en) ? machine.features_en.join(', ') : machine.features_en || '',
    is_active: Boolean(machine.is_active) // Boolean'a çevir
  })
  isEditing.value = true
  showModal.value = true
}

const closeModal = () => {
  showModal.value = false
  resetForm()
}

const resetForm = () => {
  Object.assign(form, {
    id: null,
    brand: '',
    name_tr: '',
    name_en: '',
    category_tr: '',
    category_en: '',
    image_url: '',
    drilling_diameter: '',
    drilling_depth: '',
    motor_power: '',
    weight: '',
    specifications: '',
    features_tr: '',
    features_en: '',
    sort_order: 0,
    is_active: true
  })
}

const submitForm = async () => {
  try {
    submitting.value = true
    
    // Features alanlarını array'e çevir
    const machineData = {
      ...form,
      features_tr: form.features_tr ? form.features_tr.split(',').map(f => f.trim()) : [],
      features_en: form.features_en ? form.features_en.split(',').map(f => f.trim()) : [],
      specifications: form.specifications || {}
    }
    
    let response
    if (isEditing.value) {
      response = await adminApi.updateMachine(form.id, machineData)
    } else {
      response = await adminApi.createMachine(machineData)
    }
    
    if (response.success) {
      closeModal()
      await loadMachinery()
      alert(response.message || 'Işlem başarılı!')
    } else {
      throw new Error(response.message || 'Işlem başarısız')
    }
  } catch (error) {
    console.error('Makine kaydedilemedi:', error)
    alert('Makine kaydedilemedi: ' + error.message)
  } finally {
    submitting.value = false
  }
}

const toggleActive = async (machine) => {
  try {
    const currentStatus = machine.is_active === 1 || machine.is_active === '1' || machine.is_active === true
    const newStatus = !currentStatus
    
    console.log(`Toggling machine ${machine.id} from ${currentStatus} to ${newStatus}`)
    
    const response = await adminApi.updateMachine(machine.id, { is_active: newStatus })
    
    if (response.success) {
      // API'den dönen güncel veriyi kullan
      if (response.data && typeof response.data.is_active !== 'undefined') {
        const updatedIsActive = response.data.is_active === 1 || response.data.is_active === '1' || response.data.is_active === true
        machine.is_active = updatedIsActive
        console.log(`Machine ${machine.id} updated - API returned:`, response.data.is_active, '→ Converted:', updatedIsActive)
      } else {
        machine.is_active = newStatus
      }
      
      // Update real totals when machine status changes
      if (machine.is_active) {
        realActiveMachinery.value += 1
      } else {
        realActiveMachinery.value -= 1
      }
      
      await updateStats()
      
      // Success message
      const statusText = machine.is_active ? 'aktif' : 'pasif'
      console.log(`Makine başarıyla ${statusText} duruma getirildi`)
    } else {
      throw new Error(response.message || 'Durum güncellenemedi')
    }
  } catch (error) {
    console.error('Makine durumu güncellenemedi:', error)
    alert('Makine durumu güncellenemedi: ' + error.message)
  }
}

const deleteMachine = async (machine) => {
  if (!confirm(`"${machine.name_tr}" makinesini silmek istediğinize emin misiniz?`)) {
    return
  }

  try {
    const response = await adminApi.deleteMachine(machine.id)
    
    if (response.success) {
      await loadMachinery()
      alert(response.message || 'Makine başarıyla silindi')
    } else {
      throw new Error(response.message || 'Silme işlemi başarısız')
    }
  } catch (error) {
    console.error('Makine silinemedi:', error)
    alert('Makine silinemedi: ' + error.message)
  }
}

const handleImageError = (event) => {
  event.target.src = '/images/default-machine.jpg'
}

const debouncedSearch = (() => {
  let timeout
  return () => {
    clearTimeout(timeout)
    timeout = setTimeout(() => {
      loadMachinery()
    }, 300)
  }
})()

// Watchers
watch(filterActive, () => {
  currentPage.value = 1 // Reset to first page when filter changes
  loadMachinery()
})

// Initialize
onMounted(() => {
  loadMachinery()
})
</script>

<style scoped>
.admin-machinery {
  padding: 20px;
  max-width: 1400px;
  margin: 0 auto;
}

/* Header */
.admin-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 2px solid #eee;
}

.admin-title {
  font-size: 28px;
  color: #2c3e50;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 12px;
}

.admin-title i {
  color: #e74c3c;
}

/* Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  display: flex;
  align-items: center;
  gap: 15px;
}

.stat-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: #95a5a6;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.stat-icon.active {
  background: #27ae60;
}

.stat-number {
  font-size: 24px;
  font-weight: bold;
  color: #2c3e50;
  margin: 0;
}

.stat-label {
  font-size: 14px;
  color: #7f8c8d;
  margin: 0;
}

/* Filters */
.admin-filters {
  display: flex;
  gap: 20px;
  margin-bottom: 30px;
  align-items: center;
}

.search-box {
  position: relative;
  flex: 1;
  max-width: 400px;
}

.search-box i {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #7f8c8d;
}

.search-box input {
  width: 100%;
  padding: 12px 12px 12px 40px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
}

.filter-select {
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: white;
  font-size: 14px;
  min-width: 150px;
}

/* Loading & Empty States */
.loading-state, .empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #7f8c8d;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #e74c3c;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.empty-state i {
  font-size: 48px;
  color: #ddd;
  margin-bottom: 20px;
}

/* Machinery Grid */
.admin-machinery-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 20px;
}

.machinery-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  overflow: hidden;
  position: relative;
  transition: transform 0.2s ease;
}

.machinery-card:hover {
  transform: translateY(-2px);
}

.machinery-image {
  height: 200px;
  overflow: hidden;
}

.machinery-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.machinery-info {
  padding: 20px;
}

.machinery-brand {
  background: #e74c3c;
  color: white;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  display: inline-block;
  margin-bottom: 10px;
}

.machinery-name {
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 5px 0;
}

.machinery-name-en {
  font-size: 14px;
  color: #7f8c8d;
  margin: 0 0 15px 0;
  font-style: italic;
}

.machinery-specs {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 15px;
}

.spec-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #5a6c7d;
}

.spec-item i {
  color: #e74c3c;
  width: 12px;
}

.machinery-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
}

.machinery-status {
  padding: 4px 12px;
  border-radius: 20px;
  font-weight: 600;
}

.machinery-status.active {
  background: #d5f4e6;
  color: #27ae60;
}

.machinery-status.inactive {
  background: #fadbd8;
  color: #e74c3c;
}

.machinery-order {
  color: #7f8c8d;
}

.machinery-actions {
  position: absolute;
  top: 15px;
  right: 15px;
  display: flex;
  gap: 5px;
}

.btn-status, .btn-edit, .btn-delete {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  font-size: 12px;
}

.btn-status {
  background: #95a5a6;
  color: white;
}

.btn-status.active {
  background: #27ae60;
}

.btn-status.inactive {
  background: #95a5a6;
}

.btn-edit {
  background: #3498db;
  color: white;
}

.btn-delete {
  background: #e74c3c;
  color: white;
}

.btn-status:hover, .btn-edit:hover, .btn-delete:hover {
  opacity: 0.8;
  transform: scale(1.05);
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-content {
  background: white;
  border-radius: 12px;
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 30px;
  border-bottom: 1px solid #eee;
}

.modal-header h2 {
  margin: 0;
  color: #2c3e50;
}

.modal-close {
  background: none;
  border: none;
  font-size: 20px;
  color: #7f8c8d;
  cursor: pointer;
  padding: 5px;
  border-radius: 4px;
  transition: background 0.2s ease;
}

.modal-close:hover {
  background: #f8f9fa;
}

.modal-form {
  padding: 30px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 8px;
  font-size: 14px;
}

.form-group input,
.form-group textarea,
.form-group select {
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.2s ease;
}

.form-group input:focus,
.form-group textarea:focus,
.form-group select:focus {
  outline: none;
  border-color: #e74c3c;
  box-shadow: 0 0 0 3px rgba(231, 76, 60, 0.1);
}

.form-group input[type="checkbox"] {
  width: auto;
  margin: 0;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 15px;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #eee;
}

/* Buttons */
.btn {
  padding: 12px 20px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-create {
  background: #27ae60;
  color: white;
}

.btn-primary {
  background: #e74c3c;
  color: white;
}

.btn-cancel {
  background: #95a5a6;
  color: white;
}

.btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Responsive */
@media (max-width: 768px) {
  .admin-machinery {
    padding: 15px;
  }
  
  .admin-header {
    flex-direction: column;
    align-items: stretch;
    gap: 15px;
  }
  
  .stats-grid {
    grid-template-columns: 1fr;
  }
  
  .admin-filters {
    flex-direction: column;
    align-items: stretch;
  }
  
  .search-box {
    max-width: none;
  }
  
  .admin-machinery-grid {
    grid-template-columns: 1fr;
  }
  
  .form-row {
    grid-template-columns: 1fr;
  }
  
  .modal-content {
    margin: 10px;
  }
  
  .modal-form {
    padding: 20px;
  }
}
</style>